import { useEffect, useRef, useState } from 'react';

export type WebSocketStatus = 'connecting' | 'connected' | 'reconnecting' | 'disabled';

export type UseWebSocketOptions = {
  /** When false, no connection is opened (default true). */
  enabled?: boolean;
};

export function useWebSocket<T>(
  url: string,
  onMessage: (data: T) => void,
  options?: UseWebSocketOptions
) {
  const enabled = options?.enabled !== false;
  const [status, setStatus] = useState<WebSocketStatus>(enabled && url ? 'connecting' : 'disabled');
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep the onMessage ref up to date without triggering reconnects
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!enabled || !url) {
      setStatus('disabled');
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
      return;
    }

    let allowReconnect = true;
    setStatus('connecting');

    const connect = () => {
      if (!allowReconnect) return;
      const socket = new WebSocket(url);
      socketRef.current = socket;

      socket.onopen = () => {
        if (!allowReconnect) return;
        setStatus('connected');
        console.log(`Connected to WebSocket server at ${url}`);
      };

      socket.onmessage = (event) => {
        if (!allowReconnect) return;
        try {
          const message = JSON.parse(event.data);
          onMessageRef.current(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      socket.onclose = () => {
        console.log(`Disconnected from WebSocket server at ${url}`);
        if (!allowReconnect) return;
        setStatus('reconnecting');
        reconnectTimerRef.current = setTimeout(connect, 5000);
      };

      socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        socket.close(); // onclose will trigger the reconnect logic
      };
    };

    connect();

    return () => {
      allowReconnect = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [url, enabled]);

  return status;
}
